import { fg } from '@atlaskit/platform-feature-flags';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import { type SelectFilterState } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, type ProductKeys3P } from '../../../../../common/constants/products';
import { type SelectFilterOption } from '../../../../../common/ui/filters/select-filter';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';
import { getCachedThirdPartyFilterOptions } from '../utils/third-party-filter-options-cache';

const salesforceProducts: ProductKeys[] = [ProductKeys.Salesforce];
const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('salesforceStage');

const fetchDynamicStageOptions = async (config: CloudConfig): Promise<SelectFilterOption[]> => {
	const cloudId = config.cloudId;
	const datasourceId = config.selectedDatasource?.datasourceId;

	if (!cloudId || !datasourceId) {
		return [];
	}

	try {
		const result = await getCachedThirdPartyFilterOptions(config);

		if (!result) {
			return [];
		}

		const opportunityStages = result.filters.find((f) => f.name === 'opportunityStages');
		if (!opportunityStages || opportunityStages.values.length === 0) {
			return [];
		}

		return opportunityStages.values.map((stage) => ({
			value: stage.name,
			trackingKey: stage.name,
			queryParamValue: stage.name,
			isDefault: false,
			label: stage.label ?? stage.name,
			product: config.product,
			isSelected: false,
			isSuggested: false,
		}));
	} catch {
		return [];
	}
};

/**
 * Returns stage options — dynamic when the feature flag is on, and
 * an empty list when the feature flag is off.
 */
const getStageOptions = async (config: CloudConfig): Promise<SelectFilterOption[]> => {
	if (fg('rovo-fps-3p-ux-salesforce-dynamic-stage-filter')) {
		return fetchDynamicStageOptions(config);
	}
	return [];
};

const fetchQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const allOptions = await getStageOptions(config);
	const matched = allOptions.filter((option) =>
		queryParams.find((queryParam) => option.value === queryParam),
	);

	// If the API fetch returned no options (e.g. datasource not yet initialised on
	// initial URL load), fall back to synthetic options built from the raw query param
	// values so the store reflects hasSelection=true and shows the selected label in
	// the UI before the async fetch resolves.
	// Only apply this fallback when the flag is on — when off, the filter is disabled
	// and should not hydrate any state from query params.
	if (
		matched.length === 0 &&
		queryParams.length > 0 &&
		fg('rovo-fps-3p-ux-salesforce-dynamic-stage-filter')
	) {
		return queryParams.map((queryParam) => ({
			value: queryParam,
			trackingKey: queryParam,
			queryParamValue: queryParam,
			isDefault: false,
			label: queryParam,
			isSelected: true,
			isSuggested: false,
			product: config.product,
		}));
	}

	return matched;
};

export const filter: Readonly<{
	id: 'salesforceStage';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'salesforceStage'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'salesforceStage'>;
	getInitialState: (
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'salesforceStage'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'salesforceStage',
	products: [...salesforceProducts, ...products3PFromConfig],
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'salesforceStages',
		fetcher: async (queryParams, config) => {
			return fetchQueryParams(queryParams, config);
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return getStageOptions(config);
		},
	},
});
